/** @format */
import { Thumbnail } from '@mpb-com/feather/components/atoms/thumbnail';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import { getModelHrefFromSlug } from '@toucan/common/services/model/helpers';

import { CloudflareImageSize } from '@toucan/client/global/cloudflare';
import { MarketContext } from '@toucan/client/global/market';
import { fallbackImagePath } from '@toucan/client/global/product';

import {
    Link,
    LinkTarget,
    LinkVariant,
} from '@toucan/client/shared/components/link';

import { CloudflareImage } from '@toucan/client/blocks/components/cloudflare-image';

import { DEFAULT_TEST_ID } from './config/constants';
import { KitModelRowProps } from './config/types';
import { styles } from './kit-model-row.styles';

export * from './config/constants';
export type { KitModelRowProps } from './config/types';

const StyledKitModelRow = styled.li`
    ${styles}
`;

/**
 * One piece of kit named on a card: a thumbnail, its name — which links out
 * to that model's own page — what kind of kit it is, and the share of that
 * card's buyers who added it. The name is the row's only interactive part.
 */
export const KitModelRow: FunctionComponent<KitModelRowProps> = ({
    model,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const { name, urlSegment, imageUrl, tag, sharePct } = model;

    return (
        <StyledKitModelRow data-testid={dataTestId} {...otherProps}>
            <Thumbnail
                className="kit-model-row-thumbnail"
                width="var(--kit-model-row__thumbnail-size)"
                height="var(--kit-model-row__thumbnail-size)"
            >
                <CloudflareImage
                    key={imageUrl}
                    src={imageUrl ?? fallbackImagePath}
                    fallbackSrc={fallbackImagePath}
                    // The name sits right next to the image, so alt text
                    // would only repeat it.
                    alt=""
                    imageSize={CloudflareImageSize.THUMBNAIL}
                />
            </Thumbnail>
            <span className="kit-model-row-details">
                <Link
                    className="kit-model-row-name"
                    variant={LinkVariant.CUSTOM}
                    href={getModelHrefFromSlug(
                        t,
                        activeMarket.slug,
                        urlSegment
                    )}
                    target={LinkTarget.BLANK}
                    rel="noopener noreferrer"
                    data-testid={`${dataTestId}__name-link`}
                >
                    {name}
                </Link>
                <span className="kit-model-row-tag">{tag}</span>
            </span>
            <span
                className="kit-model-row-share"
                data-testid={`${dataTestId}__share`}
            >
                {sharePct}%
            </span>
        </StyledKitModelRow>
    );
};
